import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash, PaperPlaneTilt, Plus, Receipt, FloppyDisk, X, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, formatApiErrorDetail, openPdf } from "../../lib/api";

const money = (v) => `${Number(v || 0).toFixed(2)} $`;
const statusColor = {
  new: "bg-gold/15 text-gold-dark",
  sent: "bg-leaf/15 text-forest",
  converted: "bg-forest/10 text-forest",
};

export default function QuotesPanel({ onChanged, onConvert }) {
  const [quotes, setQuotes] = useState([]);
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);
  const [taxRate, setTaxRate] = useState(13);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/quotes");
      setQuotes(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const open = (q) => {
    setActive(q);
    setItems(q.items?.length ? q.items : [{ description: "", quantity: 1, unit_price: 0 }]);
    setTaxRate(q.tax_rate ?? 13);
    setNote(q.admin_note || "");
  };

  const subtotal = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;

  const updateItem = (idx, k, v) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((a) => [...a, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (idx) => setItems((a) => a.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const clean = items
        .filter((i) => i.description)
        .map((i) => ({ description: i.description, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 }));
      const { data } = await api.patch(`/admin/quotes/${active.id}`, { items: clean, tax_rate: Number(taxRate), admin_note: note });
      setActive(data);
      await load();
      toast.success("Devis enregistré");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    await save();
    try {
      const { data } = await api.post(`/admin/quotes/${active.id}/send`);
      if (data.email?.status === "sent") toast.success("Devis envoyé par email");
      else if (data.email?.status === "skipped") toast.info("Devis prêt (email non configuré)");
      else toast.warning("Devis marqué envoyé (email: " + (data.email?.status) + ")");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const convert = async () => {
    const clean = items
      .filter((i) => i.description)
      .map((i) => ({ description: i.description, quantity: Number(i.quantity) || 1, unit_price: Number(i.unit_price) || 0 }));
    if (!clean.length) return toast.error("Ajoutez au moins une ligne");
    onConvert?.({
      quote_id: active.id, client_name: active.name, client_email: active.email,
      client_phone: active.phone, items: clean, tax_rate: Number(taxRate), note,
    });
    setActive(null);
  };

  const remove = async (id) => {
    await api.delete(`/admin/quotes/${id}`);
    await load();
    onChanged?.();
    toast.success("Supprimé");
  };

  const downloadPdf = async () => {
    await save();
    try {
      await openPdf(`/admin/quotes/${active.id}/pdf`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur PDF");
    }
  };

  return (
    <div data-testid="quotes-panel">
      <div className="grid gap-3">
        {quotes.length === 0 && <div className="rounded-2xl border border-dashed border-forest/20 p-10 text-center text-forest/50">Aucun devis pour le moment.</div>}
        {quotes.map((q, i) => (
          <motion.div
            key={q.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-white border border-forest/10 p-5 hover:border-forest/25 transition-colors"
            data-testid={`quote-row-${i}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="font-bold text-forest">{q.name}</span>
                <Badge className={`${statusColor[q.status] || "bg-forest/10 text-forest"} border-0 capitalize`}>{q.status}</Badge>
              </div>
              <div className="mt-1 text-sm text-forest/60 truncate">{q.email} · {q.phone || "—"} · {q.property_type} · {q.city}</div>
              <div className="mt-1 text-xs text-forest/45">{(q.services || []).join(", ")}</div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => open(q)} className="rounded-full bg-forest px-4 py-2 text-xs font-bold text-white hover:bg-forest-dark transition-colors" data-testid={`quote-open-${i}`}>
                Préparer le devis
              </button>
              <button onClick={() => remove(q.id)} className="grid h-9 w-9 place-items-center rounded-full border border-destructive/20 text-destructive hover:bg-destructive/10 transition-colors" data-testid={`quote-delete-${i}`}>
                <Trash size={16} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">Devis · {active?.name}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-5">
              <div className="rounded-xl bg-sage p-4 text-sm text-forest/70">
                {active.email} · {active.phone || "—"}<br />{active.property_type} · {active.city} · {active.surface || "—"}
                {active.message && <div className="mt-2 italic">&laquo;&nbsp;{active.message}&nbsp;&raquo;</div>}
              </div>

              <div className="space-y-2">
                <Label>Lignes du devis</Label>
                {items.map((it, idx) => (
                  <div key={idx} className="flex gap-2 items-center" data-testid={`quote-item-${idx}`}>
                    <Input placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="rounded-lg h-10 flex-1" data-testid={`item-desc-${idx}`} />
                    <Input type="number" placeholder="Qté" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="rounded-lg h-10 w-16" data-testid={`item-qty-${idx}`} />
                    <Input type="number" placeholder="Prix" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} className="rounded-lg h-10 w-24" data-testid={`item-price-${idx}`} />
                    <button onClick={() => removeItem(idx)} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-destructive hover:bg-destructive/10"><X size={16} /></button>
                  </div>
                ))}
                <button onClick={addItem} className="inline-flex items-center gap-1.5 text-sm font-bold text-leaf hover:text-forest transition-colors" data-testid="add-item">
                  <Plus size={16} weight="bold" /> Ajouter une ligne
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Taxes (%)</Label>
                  <Input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="rounded-lg h-10" data-testid="tax-rate" />
                </div>
                <div className="rounded-xl bg-forest text-white p-4 text-right">
                  <div className="text-xs text-white/60">Sous-total {money(subtotal)} · Taxes {money(tax)}</div>
                  <div className="font-serif text-2xl">Total {money(total)}</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="rounded-xl" data-testid="quote-note" />
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-3 text-sm font-bold text-forest hover:bg-forest hover:text-white transition-colors disabled:opacity-60" data-testid="quote-save">
                  <FloppyDisk size={16} /> Enregistrer
                </button>
                <button onClick={downloadPdf} className="inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-3 text-sm font-bold text-forest hover:bg-forest hover:text-white transition-colors" data-testid="quote-pdf">
                  <FilePdf size={16} weight="fill" /> PDF / Imprimer
                </button>
                <button onClick={send} className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-3 text-sm font-bold text-white hover:bg-forest-dark transition-colors" data-testid="quote-send">
                  <PaperPlaneTilt size={16} weight="fill" /> Envoyer par email
                </button>
                <button onClick={convert} className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-bold text-white hover:bg-gold-dark transition-colors" data-testid="quote-convert">
                  <Receipt size={16} weight="fill" /> Créer une facture
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
